import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import './RateReservation.css';

const BASE_API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const RateReservation = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [reservationData, setReservationData] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchReservationData();
  }, [reservationId]);

  const fetchReservationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_API}/restaurants/rate/reservation/${reservationId}`);
      setReservationData(response.data);
      setError('');
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404) {
        setError('Reservation not found. Please check your link.');
      } else {
        setError('Failed to load reservation details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const submitRating = async () => {
    if (!rating) {
      setError('Please select a rating');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await axios.post(`${BASE_API}/restaurants/rate/reservation/${reservationId}`, {
        stars: rating,
        comment: comment.trim() || undefined,
      });
      setSuccess(true);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to submit rating. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="rate-reservation-container">
        <Navbar />
        <div className="rate-content">
          <div className="loading-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading your reservation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !reservationData) {
    return (
      <div className="rate-reservation-container">
        <Navbar />
        <div className="rate-content">
          <div className="error-card">
            <div className="error-icon">
              <i className="bi bi-exclamation-circle-fill"></i>
            </div>
            <h2>Oops!</h2>
            <p>{error}</p>
            <button className="btn-primary" onClick={() => navigate('/')}>
              <i className="bi bi-house-fill me-2"></i>
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="rate-reservation-container">
        <Navbar />
        <div className="rate-content">
          <div className="success-card">
            <div className="success-icon">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <h2>Thank You!</h2>
            <p>Your rating has been submitted successfully.</p>
            <p className="thank-you-message">
              We appreciate your feedback and look forward to serving you again at{' '}
              <strong>{reservationData?.restaurantName}</strong>!
            </p>
            <button className="btn-primary" onClick={() => navigate('/')}>
              <i className="bi bi-house-fill me-2"></i>
              Explore More Restaurants
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rate-reservation-container">
      <Navbar />
      <div className="rate-content">
        <div className="rate-card">
          {/* Header */}
          <div className="rate-header">
            <div className="restaurant-logo">
              {reservationData?.restaurantImage ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL || ''}${reservationData.restaurantImage}`}
                  alt={reservationData.restaurantName}
                />
              ) : (
                <div className="logo-placeholder">
                  <i className="bi bi-shop"></i>
                </div>
              )}
            </div>
            <h1 className="restaurant-title">{reservationData?.restaurantName}</h1>
          </div>

          {/* Visit Details */}
          <div className="visit-details">
            <div className="visit-badge">
              <i className="bi bi-check-circle-fill"></i>
              <span>Visit Completed</span>
            </div>
            <p className="guest-name">
              <i className="bi bi-person-fill me-2"></i>
              {reservationData?.guestName}
            </p>
            <p className="visit-date">
              <i className="bi bi-calendar-event-fill me-2"></i>
              {formatDate(reservationData?.date)}
            </p>
          </div>

          {/* Rating Section */}
          <div className="rating-section">
            <h2 className="rating-title">
              <i className="bi bi-star-fill me-2"></i>
              Rate Your Experience
            </h2>
            <p className="rating-subtitle">
              Your feedback helps us improve and helps others discover great dining experiences.
            </p>

            {error && (
              <div className="error-alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
              </div>
            )}

            {!reservationData?.isCompleted && (
              <div className="warning-alert">
                <i className="bi bi-info-circle-fill me-2"></i>
                This reservation is not yet marked as completed. Ratings can only be submitted for completed visits.
              </div>
            )}

            <div className="stars-container">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className={`star-btn ${(hover || rating) >= star ? 'active' : ''}`}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(star)}
                  disabled={!reservationData?.isCompleted}
                >
                  <i className={`bi ${(hover || rating) >= star ? 'bi-star-fill' : 'bi-star'}`}></i>
                </button>
              ))}
            </div>
            <p className="rating-label">
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent'}
              {!rating && 'Select your rating'}
            </p>

            {/* Comment Section */}
            <div className="comment-section">
              <label htmlFor="comment" className="comment-label">
                <i className="bi bi-chat-quote-fill me-2"></i>
                Share your thoughts (optional)
              </label>
              <textarea
                id="comment"
                className="comment-input"
                placeholder="Tell us about your experience..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                disabled={!reservationData?.isCompleted}
              />
            </div>

            {/* Submit Button */}
            <button
              className="submit-btn"
              onClick={submitRating}
              disabled={!rating || submitting || !reservationData?.isCompleted}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="bi bi-send-fill me-2"></i>
                  Submit Rating
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RateReservation;
